import type {
  AvailableSlot,
  AvailabilityNextResponse,
  Booking,
  BookingCancellationInput,
  BookingCheckout,
  BookingExtraSelection,
  BookingParticipant,
  BookingParticipantInput,
  BookingRequest,
  BookingUpdateInput,
  BookingUpdateResult,
  RecurringSeries,
  RecurringSeriesCheckout,
  RecurringSeriesCheckoutInput,
  RecurringSeriesPreview,
  RecurringSeriesPreviewInput,
  SignedAction,
  SlotHoldRequest,
  SlotHoldResult,
  StaffBookingRequest
} from "@kuroo/contracts";
import type { RecurringBookingOccurrence, RecurringSeriesInput } from "./client-auth-types";

export type SdkBookingContext = {
  request: <T>(path: string, init?: RequestInit) => Promise<T>;
};

export function slots(context: SdkBookingContext, serviceId: string, date: string, options: { providerId?: string; locationId?: string; extras?: BookingExtraSelection[]; persons?: number; includePricing?: boolean; signal?: AbortSignal } = {}): Promise<AvailableSlot[]> {
  const query = new URLSearchParams({ serviceId, date });
  if (options.providerId) query.set("providerId", options.providerId);
  if (options.locationId) query.set("locationId", options.locationId);
  if (options.extras?.length) query.set("extras", JSON.stringify(options.extras));
  if (options.persons) query.set("persons", String(options.persons));
  if (options.includePricing) query.set("includePricing", "true");
  return context.request<AvailableSlot[]>(`/api/v1/availability/slots?${query.toString()}`, { signal: options.signal });
}

export function nextAvailability(context: SdkBookingContext, serviceId: string, options: { fromDate?: string; days?: number; providerId?: string; locationId?: string; signal?: AbortSignal } = {}): Promise<AvailabilityNextResponse> {
  const query = new URLSearchParams({ serviceId });
  if (options.fromDate) query.set("fromDate", options.fromDate);
  if (options.days) query.set("days", String(options.days));
  if (options.providerId) query.set("providerId", options.providerId);
  if (options.locationId) query.set("locationId", options.locationId);
  return context.request<AvailabilityNextResponse>(`/api/v1/availability/next?${query.toString()}`, { signal: options.signal });
}

export function holdSlot(context: SdkBookingContext, input: SlotHoldRequest): Promise<SlotHoldResult> {
  return context.request<SlotHoldResult>("/api/v1/availability/hold", {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export function releaseHold(context: SdkBookingContext, holdId: string, slotKey: string, quoteId?: string, holdCapability?: string): Promise<{ released: boolean }> {
  return context.request<{ released: boolean }>("/api/v1/availability/release", {
    method: "POST",
    body: JSON.stringify({ holdId, slotKey, ...(quoteId ? { quoteId } : {}), ...(holdCapability ? { holdCapability } : {}) })
  });
}

export function createBooking(context: SdkBookingContext, input: BookingRequest): Promise<Booking | BookingCheckout> {
  return context.request<Booking | BookingCheckout>("/api/v1/bookings", {
    method: "POST",
    body: JSON.stringify(input),
    headers: { "Idempotency-Key": input.idempotencyKey }
  });
}

export function createAdminBooking(context: SdkBookingContext, input: StaffBookingRequest): Promise<Booking | BookingCheckout> {
  return context.request<Booking | BookingCheckout>("/api/v1/admin/bookings", {
    method: "POST",
    body: JSON.stringify(input),
    headers: { "Idempotency-Key": input.idempotencyKey }
  });
}

export function recurringSeries(context: SdkBookingContext): Promise<RecurringSeries[]> {
  return context.request<RecurringSeries[]>("/api/v1/recurring-series");
}

export function recurringSeriesDetail(context: SdkBookingContext, seriesId: string): Promise<RecurringSeries> {
  return context.request<RecurringSeries>(`/api/v1/recurring-series/${encodeURIComponent(seriesId)}`);
}

export function previewRecurringSeries(context: SdkBookingContext, input: RecurringSeriesPreviewInput, options: { signal?: AbortSignal } = {}): Promise<RecurringSeriesPreview> {
  return context.request<RecurringSeriesPreview>("/api/v1/recurring-series/preview", { method: "POST", body: JSON.stringify(input), signal: options.signal });
}

/** Calculates and serializes every included occurrence of a recurring proposal. */
export function previewRecurringSeriesAndHold(context: SdkBookingContext, input: RecurringSeriesPreviewInput & { idempotencyKey: string }, options: { signal?: AbortSignal } = {}): Promise<RecurringSeriesPreview> {
  return context.request<RecurringSeriesPreview>("/api/v1/recurring-series/preview-and-hold", { method: "POST", body: JSON.stringify(input), signal: options.signal, headers: { "Idempotency-Key": input.idempotencyKey } });
}

export function checkoutRecurringSeries(context: SdkBookingContext, input: RecurringSeriesCheckoutInput): Promise<RecurringSeriesCheckout> {
  return context.request<RecurringSeriesCheckout>("/api/v1/recurring-series/checkout", { method: "POST", body: JSON.stringify(input), headers: { "Idempotency-Key": input.idempotencyKey } });
}

export function createRecurringSeries(context: SdkBookingContext, input: RecurringSeriesInput): Promise<RecurringSeries> {
  return context.request<RecurringSeries>("/api/v1/recurring-series", { method: "POST", body: JSON.stringify(input) });
}

export function cancelRecurringSeries(context: SdkBookingContext, seriesId: string, input: { scope?: "occurrence" | "future" | "all"; occurrenceIds?: string[]; reason?: string } = {}): Promise<{ id: string; status: string; refundId?: string | null; refundPending?: boolean }> {
  return context.request<{ id: string; status: string; refundId?: string | null; refundPending?: boolean }>(`/api/v1/recurring-series/${encodeURIComponent(seriesId)}/cancel`, { method: "POST", body: JSON.stringify(input) });
}

export function rescheduleRecurringOccurrence(context: SdkBookingContext, seriesId: string, occurrenceId: string, input: { slotKey: string; startAt: string; endAt: string; providerId?: string | null; locationId?: string | null; idempotencyKey: string }): Promise<RecurringSeries> {
  return context.request<RecurringSeries>(`/api/v1/recurring-series/${encodeURIComponent(seriesId)}/occurrences/${encodeURIComponent(occurrenceId)}/reschedule`, { method: "POST", body: JSON.stringify(input), headers: { "Idempotency-Key": input.idempotencyKey } });
}

export function createRecurringBookings(context: SdkBookingContext, seriesId: string, occurrences: RecurringBookingOccurrence[], options: { persons?: number; notes?: string; idempotencyKey?: string } = {}): Promise<{ seriesId: string; bookings: Booking[] }> {
  const idempotencyKey = options.idempotencyKey ?? `recurring-${seriesId}-${Date.now()}`;
  return context.request<{ seriesId: string; bookings: Booking[] }>("/api/v1/bookings/recurring", {
    method: "POST",
    body: JSON.stringify({ seriesId, occurrences, persons: options.persons, notes: options.notes, idempotencyKey }),
    headers: { "Idempotency-Key": idempotencyKey }
  });
}

export function bookingParticipants(context: SdkBookingContext, bookingId: string): Promise<BookingParticipant[]> {
  return context.request<BookingParticipant[]>(`/api/v1/bookings/${encodeURIComponent(bookingId)}/participants`);
}

export function addBookingParticipant(context: SdkBookingContext, bookingId: string, input: BookingParticipantInput): Promise<BookingParticipant> {
  return context.request<BookingParticipant>(`/api/v1/bookings/${encodeURIComponent(bookingId)}/participants`, { method: "POST", body: JSON.stringify(input) });
}

export function removeBookingParticipant(context: SdkBookingContext, bookingId: string, participantId: string): Promise<{ id: string; deleted: boolean }> {
  return context.request<{ id: string; deleted: boolean }>(`/api/v1/bookings/${encodeURIComponent(bookingId)}/participants/${encodeURIComponent(participantId)}`, { method: "DELETE" });
}

export function reassignBooking(context: SdkBookingContext, bookingId: string, input: { slotKey: string; startAt: string; endAt: string; providerId?: string; locationId?: string; idempotencyKey?: string }): Promise<Booking> {
  const idempotencyKey = input.idempotencyKey ?? `reassign-${bookingId}-${Date.now()}`;
  return context.request<Booking>(`/api/v1/bookings/${encodeURIComponent(bookingId)}/reassign`, { method: "POST", body: JSON.stringify({ ...input, idempotencyKey }), headers: { "Idempotency-Key": idempotencyKey } });
}

export function createBookingAction(context: SdkBookingContext, bookingId: string, action: "cancel" = "cancel", ttlSeconds?: number): Promise<SignedAction> {
  return context.request<SignedAction>(`/api/v1/bookings/${encodeURIComponent(bookingId)}/signed-action`, { method: "POST", body: JSON.stringify({ action, ttlSeconds }) });
}

export function executeSignedAction(context: SdkBookingContext, token: string): Promise<{ action: string; id: string; status: string }> {
  return context.request<{ action: string; id: string; status: string }>(`/api/v1/signed-actions/${encodeURIComponent(token)}`, { method: "POST", body: "{}" });
}

export function bookings(context: SdkBookingContext): Promise<Booking[]> {
  return context.request<Booking[]>("/api/v1/bookings");
}

export function booking(context: SdkBookingContext, bookingId: string): Promise<Booking> {
  return context.request<Booking>(`/api/v1/bookings/${encodeURIComponent(bookingId)}`);
}

export function updateBooking(context: SdkBookingContext, bookingId: string, input: BookingUpdateInput): Promise<BookingUpdateResult> {
  return context.request<BookingUpdateResult>(`/api/v1/bookings/${encodeURIComponent(bookingId)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
    headers: { "Idempotency-Key": input.idempotencyKey }
  });
}

export function cancelBooking(context: SdkBookingContext, bookingId: string, input: BookingCancellationInput = {}): Promise<{ id: string; status: "cancelled"; refundId?: string | null; refundEligible?: boolean }> {
  return context.request<{ id: string; status: "cancelled"; refundId?: string | null; refundEligible?: boolean }>(`/api/v1/bookings/${encodeURIComponent(bookingId)}/cancel`, { method: "POST", body: JSON.stringify(input) });
}
